import { useState, useEffect, useCallback } from 'react';
import { Search, Filter, ChevronDown } from 'lucide-react';
import { api, buildQuery } from '../lib/api';
import type { Property, State, Area, PriceRange, Currency } from '../types';
import PropertyCard from './PropertyCard';
import { Spinner } from './ui';
import ImageHero from './ImageHero';

interface PropertySearchProps {
  listingType?: string;
  listingTypes?: string[];
  leasingType?: 'rent' | 'shortlet';
  stateSlug?: string;
  areaSlug?: string;
  title: string;
  subtitle: string;
  allowLeasingType?: boolean;
}

interface PriceRangeSliderProps {
  min: number;
  max: number;
  step: number;
  minValue: number;
  maxValue: number;
  currency?: string;
  onChange: (minValue: number, maxValue: number) => void;
}

const trimNumber = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, ''));

const formatAmount = (v: number, currency?: string) => {
  if (currency === 'NGN') {
    if (v >= 1e9) return `\u20A6${trimNumber(v / 1e9)}B`;
    if (v >= 1e6) return `\u20A6${trimNumber(v / 1e6)}M`;
    return `\u20A6${v.toLocaleString()}`;
  }
  if (v >= 1e6) return `$${trimNumber(v / 1e6)}M`;
  if (v >= 1e3) return `$${trimNumber(v / 1e3)}K`;
  return `$${v.toLocaleString()}`;
};

const sliderThumbClasses =
  '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:pointer-events-auto ' +
  '[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full ' +
  '[&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white ' +
  '[&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 ' +
  '[&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary ' +
  '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md';

function PriceRangeSlider({ min, max, step, minValue, maxValue, currency, onChange }: PriceRangeSliderProps) {
  const percent = (v: number) => ((v - min) / (max - min)) * 100;
  const leftPct = percent(Math.min(minValue, max));
  const rightPct = 100 - percent(Math.max(maxValue, min));
  return (
    <div>
      <div className="flex items-center justify-between mb-2 text-sm font-medium text-charcoal">
        <span>{formatAmount(minValue, currency)}</span>
        <span>{formatAmount(maxValue, currency)}</span>
      </div>
      <div className="relative h-6">
        <div className="absolute top-1/2 -translate-y-1/2 h-2 w-full bg-ash rounded-full" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-2 bg-primary rounded-full"
          style={{ left: `${leftPct}%`, right: `${rightPct}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={minValue}
          onChange={(e) => {
            const v = Math.min(Number(e.target.value), Math.max(maxValue - step, min));
            onChange(v, maxValue);
          }}
          aria-label="Minimum price"
          className={`absolute inset-0 w-full h-full appearance-none bg-transparent pointer-events-none ${sliderThumbClasses}`}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={maxValue}
          onChange={(e) => {
            const v = Math.max(Number(e.target.value), Math.min(minValue + step, max));
            onChange(minValue, v);
          }}
          aria-label="Maximum price"
          className={`absolute inset-0 w-full h-full appearance-none bg-transparent pointer-events-none ${sliderThumbClasses}`}
        />
      </div>
    </div>
  );
}

export default function PropertySearch({
  listingType,
  listingTypes,
  leasingType,
  stateSlug,
  areaSlug,
  title,
  subtitle,
  allowLeasingType,
}: PropertySearchProps) {
  const [states, setStates] = useState<State[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [ranges, setRanges] = useState<PriceRange[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [stateName, setStateName] = useState('');
  const [area, setArea] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [leaseType, setLeaseType] = useState('');
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceMin, setPriceMin] = useState<number | null>(null);
  const [priceMax, setPriceMax] = useState<number | null>(null);
  const [priceTouched, setPriceTouched] = useState(false);
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);

  const currencyFor = (name: string): Currency => {
    const s = states.find((x) => x.name === name);
    if (s && s.currency === 'USD') return 'USD';
    if (/dubai/i.test(name)) return 'USD';
    return 'NGN';
  };

  const currency = currencyFor(stateName);
  const activeRange = ranges.find((r) => r.currency === currency) || ranges[0] || null;

  useEffect(() => {
    api.get<State[]>('/states').then(setStates).catch(() => {});
    api.get<Area[]>('/areas').then(setAreas).catch(() => {});
    api.get<PriceRange[]>('/price-ranges').then((rows) => {
      if (rows.length) {
        setRanges(rows);
        const ngn = rows.find((r) => r.currency === 'NGN') || rows[0];
        setPriceMin(ngn.min_price);
        setPriceMax(ngn.max_price);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (stateSlug) {
      const match = states.find((s) => s.slug === stateSlug);
      if (match) setStateName(match.name);
    }
  }, [stateSlug, states]);

  useEffect(() => {
    if (areaSlug) {
      const match = areas.find((a) => a.slug === areaSlug);
      if (match) setArea(match.name);
    }
  }, [areaSlug, areas]);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveListingType = listingTypes && listingTypes.length ? listingTypes.join(',') : listingType;
      const priceFilter = priceTouched && activeRange
        ? {
            currency: activeRange.currency,
            ...(priceMin !== null && priceMin > activeRange.min_price ? { min_price: priceMin } : {}),
            ...(priceMax !== null && priceMax < activeRange.max_price ? { max_price: priceMax } : {}),
          }
        : {};
      const params: Record<string, string | number | undefined> = {
        listing_type: effectiveListingType,
        leasing_type: leasingType || leaseType || undefined,
        state: stateName || undefined,
        area: area || undefined,
        property_type: propertyType || undefined,
        bedrooms: bedrooms || undefined,
        ...priceFilter,
        page,
        limit: 9,
        sort: sort === 'newest' ? undefined : sort,
      };
      const data = await api.get<{ items: Property[]; total: number; totalPages: number }>(
        `/properties${buildQuery(params)}`
      );
      setProperties(data.items);
      setTotal(data.total);
    } catch {
      setProperties([]);
    } finally {
      setLoading(false);
    }
    }, [listingType, listingTypes, leasingType, leaseType, stateName, area, propertyType, bedrooms, activeRange, priceTouched, priceMin, priceMax, page, sort]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const resetFilters = () => {
    setStateName('');
    setArea('');
    setPropertyType('');
    setBedrooms('');
    setLeaseType('');
    if (activeRange) {
      setPriceMin(activeRange.min_price);
      setPriceMax(activeRange.max_price);
    }
    setPriceTouched(false);
    setPage(1);
  };

  return (
    <div>
      {/* Hero Section */}
      <ImageHero height="h-[66vh]" imageKey="hero_image_properties" fallbackClass="bg-property-hero">
        <div className="max-w-7xl mx-auto px-4 h-full flex items-start pt-[33vh]">
          <div className="text-white max-w-3xl">
            <p className="uppercase tracking-[0.35em] text-primary text-xs md:text-sm font-semibold mb-4">Properties</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4 capitalize">{title}</h1>
            <p className="uppercase tracking-[0.35em] text-white text-xs md:text-sm font-semibold">{subtitle}</p>
          </div>
        </div>
      </ImageHero>

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Search Section */}
          <div className="bg-white p-6 rounded-lg shadow-lg mb-8 border-t-4 border-primary">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Location</label>
                <select
                  value={stateName}
                  onChange={(e) => {
                    const name = e.target.value;
                    setStateName(name);
                    setArea('');
                    const r = ranges.find((x) => x.currency === currencyFor(name)) || ranges[0];
                    if (r) { setPriceMin(r.min_price); setPriceMax(r.max_price); }
                    setPriceTouched(false);
                    setPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Locations</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Area / Neighborhood</label>
                <select
                  value={area}
                  onChange={(e) => { setArea(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Areas</option>
                  {areas.filter((a) => !stateName || a.state_name === stateName).map((a) => (
                    <option key={a.id} value={a.name}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Property Type</label>
                <select
                  value={propertyType}
                  onChange={(e) => { setPropertyType(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Types</option>
                  <option value="bungalow">Bungalow</option>
                  <option value="flat">Flat</option>
                  <option value="duplex">Duplex</option>
                  <option value="terrace">Terrace</option>
                  <option value="semi-detached">Semi-Detached</option>
                  <option value="fully-detached">Fully Detached</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Bedrooms</label>
                <select
                  value={bedrooms}
                  onChange={(e) => { setBedrooms(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Any</option>
                  <option value="1">1 Bedroom</option>
                  <option value="2">2 Bedrooms</option>
                  <option value="3">3 Bedrooms</option>
                  <option value="4+">4+ Bedrooms</option>
                </select>
              </div>

              {allowLeasingType && (
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Lease Type</label>
                  <select
                    value={leaseType}
                    onChange={(e) => { setLeaseType(e.target.value); setPage(1); }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Types</option>
                    <option value="rent">Rent</option>
                    <option value="shortlet">Shortlet</option>
                  </select>
                </div>
              )}

              <div className="lg:col-span-2 xl:col-span-2">
                <label className="block text-sm font-medium text-charcoal mb-1">Price Range</label>
                <button
                  type="button"
                  onClick={() => setPriceOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <span className="text-sm text-gray-700">
                    {priceTouched && activeRange
                      ? `${formatAmount(priceMin ?? activeRange.min_price, activeRange.currency)} - ${formatAmount(priceMax ?? activeRange.max_price, activeRange.currency)}`
                      : 'Any Price'}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${priceOpen ? 'rotate-180' : ''}`} />
                </button>
                {priceOpen && (
                  <div className="mt-3">
                    {activeRange ? (
                      <>
                        <PriceRangeSlider
                          min={activeRange.min_price}
                          max={activeRange.max_price}
                          step={Math.max(activeRange.step, 1)}
                          minValue={priceMin ?? activeRange.min_price}
                          maxValue={priceMax ?? activeRange.max_price}
                          currency={activeRange.currency}
                          onChange={(mn, mx) => {
                            setPriceMin(mn);
                            setPriceMax(mx);
                            setPriceTouched(true);
                            setPage(1);
                          }}
                        />
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-xs text-gray-400">Drag the handles to set the price range</p>
                          <button
                            type="button"
                            onClick={() => {
                              setPriceMin(activeRange.min_price);
                              setPriceMax(activeRange.max_price);
                              setPriceTouched(false);
                              setPage(1);
                            }}
                            className="text-sm text-black hover:text-gray-600"
                          >
                            Reset
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-2 border border-gray-300 rounded-lg text-taupe">No price ranges configured</div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Sort By</label>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={fetchProperties}
                  className="w-full bg-primary text-black px-4 py-2 font-semibold hover:bg-primary-light transition-colors flex items-center justify-center"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-taupe">{total} {total === 1 ? 'property' : 'properties'} found</p>
              <button onClick={resetFilters} className="text-sm text-black hover:text-gray-600 flex items-center">
                <Filter className="w-4 h-4 mr-1" /> Clear Filters
              </button>
            </div>
          </div>

          {/* Properties Grid */}
          {loading ? (
            <Spinner label="Loading properties..." />
          ) : properties.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              No properties match your search. Try adjusting the filters.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                {properties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
